import { ReactNode } from 'react';
import * as R from 'remeda';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { FunctionRuntime, TestResponse } from '@rossum/api-client/hooks';
import { Box, Button, Stack, Typography } from '@rossum/ui/material';
import { hookInterfacesEnabledSelector } from '../../../../../redux/modules/organizationGroup/selectors';
import { useProcessIntent } from '../../../../extension-interop/hooks/useProcessIntent';
import { runtimesConfig } from '../../extension-detail/config';
import { TabNames } from '../types';

type OutputProps = {
  children: ReactNode;
};

const Output = ({ children }: OutputProps) => (
  <Typography
    color="secondary"
    variant="body2"
    sx={{
      py: 1,
      px: 2,
      fontFamily: "'Roboto Mono Variable', monospace",
      wordBreak: 'break-all',
      wordWrap: 'break-word',
      whiteSpace: 'pre',
    }}
  >
    {children}
  </Typography>
);

type ExtensionsOutputProps = {
  activeTab: TabNames;
  children?: ReactNode;
  runtime: FunctionRuntime;
  value: TestResponse | undefined;
};

export const ExtensionsOutput = ({
  activeTab,
  children,
  runtime,
  value,
}: ExtensionsOutputProps) => {
  const { processIntent } = useProcessIntent();
  const intl = useIntl();
  const commandsEnabled = useSelector(hookInterfacesEnabledSelector);

  return (
    <Stack
      sx={{
        height: '100%',
        width: '50%',
      }}
    >
      {children}
      <Box
        sx={{
          overflow: 'auto',
          height: '100%',
          width: '100%',
          backgroundColor: t => t.palette.background.default,
        }}
      >
        {value ? (
          <>
            {R.isObjectType(value.response) &&
            'intent' in value.response &&
            commandsEnabled ? (
              <Button
                variant="outlined"
                onClick={() => processIntent(value.response)}
                sx={{ m: 1 }}
              >
                {intl.formatMessage({ id: 'features.extensionInterop.view' })}
              </Button>
            ) : null}
            <Output>
              {activeTab === 'response'
                ? JSON.stringify(value.response, undefined, 2)
                : value.log}
            </Output>
          </>
        ) : (
          <Output>
            <FormattedMessage
              id={`components.editor.outputBox.empty.${activeTab}.${runtimesConfig[runtime].type}`}
            />
          </Output>
        )}
      </Box>
    </Stack>
  );
};
